import React, { useContext, useEffect, useState } from "react";

import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  makeStyles,
} from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";
import MessagesList from "../MessagesList";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import { SocketContext } from "../../context/Socket/SocketContext";
import SolicitacaoDesenvolvimentoPreviewModal from "../SolicitacaoDesenvolvimentoPreviewModal";
import {
  createSolicitacaoDesenvolvimento,
  getSolicitacaoDesenvolvimentoPreview,
} from "../../services/solicitacaoDesenvolvimento";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
}));

export default function TicketMessagesDialog({ open, handleClose, ticketId }) {
  const history = useHistory();
  const classes = useStyles();

  const { user } = useContext(AuthContext);

  const [, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    let delayDebounceFn = null;
    if (open) {
      setLoading(true);
      delayDebounceFn = setTimeout(() => {
        const fetchTicket = async () => {
          try {
            const { data } = await api.get("/tickets/" + ticketId);

            // A permissão é decidida pelo backend (403 ERR_NO_PERMISSION);
            // ver helpers/CanAccessTicket.ts.
            setContact(data.contact);
            setTicket(data);
            setLoading(false);
          } catch (err) {
            setLoading(false);
            toastError(err);
            if (err?.response?.status === 403) {
              history.push("/tickets");
            }
          }
        };
        fetchTicket();
      }, 500);
    }
    return () => {
      if (delayDebounceFn !== null) {
        clearTimeout(delayDebounceFn);
      }
    };
  }, [ticketId, user, history, open]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    let socket = null;

    if (open) {
      const socket = socketManager.getSocket(companyId);
      socket.on("ready", () => socket.emit("joinChatBox", `${ticket.id}`));

      socket.on(`company-${companyId}-ticket`, (data) => {
        if (data.action === "update" && data.ticket.id === ticket.id) {
          // Mesclar dados preservando relações essenciais (contact, user, etc)
          setTicket((prevTicket) => ({
            ...prevTicket,
            ...data.ticket,
            // Garantir que contact não seja perdido
            contact: data.ticket.contact || prevTicket.contact,
            // Garantir que user não seja perdido
            user: data.ticket.user || prevTicket.user,
            // Garantir que queue não seja perdida
            queue: data.ticket.queue || prevTicket.queue,
            // Garantir que whatsapp não seja perdido
            whatsapp: data.ticket.whatsapp || prevTicket.whatsapp,
            // Garantir que tags não sejam perdidas
            tags: data.ticket.tags || prevTicket.tags,
            // Garantir que isGroup não seja perdido (importante para exibir nomes em grupos)
            isGroup: data.ticket.isGroup !== undefined ? data.ticket.isGroup : prevTicket.isGroup
          }));
        }

        if (data.action === "delete" && data.ticketId === ticket.id) {
          // toast.success("Ticket deleted sucessfully.");
          history.push("/tickets");
        }
      });

      socket.on(`company-${companyId}-contact`, (data) => {
        if (data.action === "update") {
          setContact((prevState) => {
            if (prevState.id === data.contact?.id) {
              return { ...prevState, ...data.contact };
            }
            return prevState;
          });
        }
      });
    }

    return () => {
      if (socket !== null) {
        socket.disconnect();
      }
    };
  }, [ticketId, ticket, history, open, socketManager]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleOpenSolicitacaoPreview = async () => {
    if (!ticket?.id) {
      toast.error(i18n.t("tickets.developmentRequest.previewTicketNotFound"));
      return;
    }

    setPreviewOpen(true);
    setPreviewData(null);
    setPreviewLoading(true);

    try {
      const data = await getSolicitacaoDesenvolvimentoPreview(ticket.id);
      setPreviewData(data);
    } catch (err) {
      toastError(err);
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCloseSolicitacaoPreview = () => {
    if (saveLoading) {
      return;
    }

    setPreviewOpen(false);
    setPreviewData(null);
  };

  const handleConfirmSolicitacao = async (observacao) => {
    if (!ticket?.id) {
      toast.error(i18n.t("tickets.developmentRequest.saveTicketNotFound"));
      return;
    }

    setSaveLoading(true);
    try {
      await createSolicitacaoDesenvolvimento(ticket.id, observacao);
      toast.success(i18n.t("tickets.developmentRequest.created"));
      setPreviewOpen(false);
      setPreviewData(null);
    } catch (err) {
      toastError(err);
    } finally {
      setSaveLoading(false);
    }
  };

  const renderTicketInfo = () => {
    // Sempre renderizar TicketInfo quando houver ticket e contact válidos
    if (ticket.id && contact.id) {
      return (
        <TicketInfo
          contact={contact}
          ticket={ticket}
          onClick={handleDrawerOpen}
        />
      );
    }
  };

  const renderMessagesList = () => {
    // Não renderiza até que o ticket esteja carregado
    if (!ticket.id) {
      return null;
    }
    
    return (
      <Box className={classes.root}>
        <MessagesList
          key={ticket.id} // Força re-render quando muda de ticket
          ticket={ticket}
          ticketId={ticket.id}
          isGroup={ticket.isGroup}
        ></MessagesList>
      </Box>
    );
  };

  return (
    <>
      <Dialog maxWidth="md" onClose={handleClose} open={open}>
        <TicketHeader loading={loading}>{renderTicketInfo()}</TicketHeader>
        <ReplyMessageProvider>{renderMessagesList()}</ReplyMessageProvider>
        <DialogActions>
          <Button
            onClick={handleOpenSolicitacaoPreview}
            color="primary"
            disabled={loading || !ticket?.id}
          >
            {i18n.t("tickets.developmentRequest.request")}
          </Button>
          <Button onClick={handleClose} color="primary">
            {i18n.t("common.close")}
          </Button>
        </DialogActions>
      </Dialog>

      <SolicitacaoDesenvolvimentoPreviewModal
        open={previewOpen}
        onClose={handleCloseSolicitacaoPreview}
        onConfirm={handleConfirmSolicitacao}
        loading={previewLoading || saveLoading}
        previewData={previewData}
      />
    </>
  );
}
